import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { IRowNode } from 'ag-grid-enterprise';
import { saveAs } from 'file-saver';
import { Subscription, from } from 'rxjs';
import { TransactionForm } from 'src/app/classes/form-groups/transaction-form';
import { Invoice, Vendor } from 'src/app/classes/models';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import { TransactionModalComponent } from 'src/app/modals/transaction-modal/transaction-modal.component';
import { ReportService } from 'src/app/services/report.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';
import { VendorService } from 'src/app/services/vendor.service';

@Component({
  selector: 'app-admin-vendor-invoices',
  templateUrl: './admin-vendor-invoices.component.html',
  styleUrls: ['./admin-vendor-invoices.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AdminVendorInvoicesComponent {
  constructor(
    private ToastService: ToastService,
    private SharedDataService: SharedDataService,
    private VendorService: VendorService,
    private ModalService: NgbModal,
    private ReportService: ReportService,
    private ChangeDetectorRef: ChangeDetectorRef
  ) {
    this.SharedDataServiceSubscription =
      this.SharedDataService.listenForEvent<number>().subscribe({
        next: ({ eventName, data }) => {
          if (eventName === 'SetVendorId' && typeof data === 'number') {
            this.VendorId = data;

            this.VendorService.getVendorV2(this.VendorId, {
              attributes: JSON.stringify(['id', 'legalName']),
              CompanyVendorPayment: JSON.stringify({
                attributes: ['id', 'InvoiceId']
              })
            }).subscribe({
              next: (Vendor) => {
                const {
                  id: VendorId,
                  legalName,
                  CompanyVendorPayments
                } = Vendor;

                this.Vendor = Vendor;

                if (!VendorId) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Vendor is missing id.',
                    { returnError: true }
                  );
                }

                if (!legalName) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Vendor is missing legal name.',
                    { returnError: true }
                  );
                }

                if (!CompanyVendorPayments) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing Client/Vendor payment information.',
                    { returnError: true }
                  );
                }

                Object.assign(this.SuppliedQueryData, {
                  id: { $in: CompanyVendorPayments.map((cvp) => cvp.InvoiceId) }
                });

                this.SharedDataService.sendEvent(
                  'SetTitle',
                  `Invoices - ${legalName}`
                );

                this.ChangeDetectorRef.markForCheck();
              }
            });
          }
        }
      });

    this.SharedDataService.sendEvent('GetVendorId');
  }

  VendorId?: number;

  SuppliedQueryData: { [key: string]: any } = {
    attributes: ['id', 'payUuid', 'totalAmt', 'paidDate', 'status']
  };
  Vendor?: Vendor;
  SharedDataServiceSubscription?: Subscription;

  ColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['edit', 'download']
  };

  getCalculatedColumnActions: ({
    rowData
  }: {
    rowData: Invoice;
  }) => GridActionColumnActions[] = ({ rowData }: { rowData: Invoice }) => {
    const { status } = rowData;

    if (!status) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Invoice is missing status.',
        { returnError: true }
      );
    }

    const actions: GridActionColumnActions[] = ['download'];

    if (['CREATED', 'PENDING'].includes(status)) {
      actions.push('edit');
    }

    return actions;
  };

  actionEvent({
    action,
    rowData,
    rowNode
  }: {
    action: GridActionColumnActions;
    rowData: Invoice;
    rowNode: IRowNode;
  }) {
    const { id: InvoiceId } = rowData;

    if (!InvoiceId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing invoice information.',
        { returnError: true }
      );
    }

    if (action === 'edit') {
      const { totalAmt } = rowData;
      const TransactionFormGroup = new TransactionForm();
      TransactionFormGroup.patchValue({
        Invoice: rowData,
        amount: totalAmt || 0
      });
      const { componentInstance, result } = this.ModalService.open(
        TransactionModalComponent,
        { backdrop: 'static', size: 'lg' }
      );

      Object.assign(componentInstance, { TransactionFormGroup });

      from(result).subscribe({
        next: (SavedInvoice: Invoice) => {
          rowNode.setData(Object.assign(rowData, SavedInvoice));
          this.ChangeDetectorRef.markForCheck();
        }
      });
    }

    if (action === 'download') {
      this.ReportService.generateInvoiceReport({
        InvoiceId,
        VendorId: this.VendorId
      }).subscribe({
        next: (reportData) => {
          saveAs(
            new Blob([reportData as ArrayBuffer]),
            `VIVEInvoice-${InvoiceId}.pdf`
          );
        }
      });
    }
  }

  ngOnDestroy(): void {
    if (this.SharedDataServiceSubscription) {
      this.SharedDataServiceSubscription.unsubscribe();
    }
  }
}
